import { useState, useEffect, useRef } from "react"
import Flatpickr from "react-flatpickr"
import { Spanish } from "flatpickr/dist/l10n/es.js"
import "flatpickr/dist/flatpickr.css"
import { HiOutlineCalendarDays } from "react-icons/hi2"
import "./date-field.css"
import type FieldProps from "../textFieldProps"
import { getResultColorClass } from "../utilites/get-color-scoring"

const DateField = ({
  model,
  setModel,
  campoId,
  placeholder,
  required,
  campoIdRespuesta,
  editable,
  title,
  tooltip,
}: FieldProps) => {
  const [resumeValue, setResumeValue] = useState<any>(undefined)
  const flatpickrRef = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const flatpickrInstanceRef = useRef<any>(null)
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (campoIdRespuesta && model.resume && model.resume[campoIdRespuesta] !== undefined) {
      setResumeValue(model.resume[campoIdRespuesta])
    }
  }, [model, campoIdRespuesta])

  const handleDateChange = (selectedDates: Date[], dateStr: string) => {
    if (!editable || !isMounted.current) return

    if (selectedDates.length > 0) {
      const dateParts = dateStr.split("/")

      if (dateParts.length === 3) {
        const day = dateParts[0].padStart(2, "0")
        const month = dateParts[1].padStart(2, "0")
        const year = dateParts[2]
        const formattedDate = `${year}-${month}-${day}`

        setModel((prevModel: any) => {
          if (!prevModel) return { [campoId]: formattedDate };
          const newModel = Object.assign(Object.create(Object.getPrototypeOf(prevModel)), prevModel);
          newModel[campoId] = formattedDate;
          return newModel;
        })
      }
    }
  }

  const [isEmpty, setIsEmpty] = useState(
    model[campoId] === undefined || model[campoId] === null || model[campoId] === "",
  )

  useEffect(() => {
    setIsEmpty(model[campoId] === undefined || model[campoId] === null || model[campoId] === "")
  }, [model, campoId])

  const updateCalendarPosition = () => {
    if (!containerRef.current || !flatpickrInstanceRef.current?.calendarContainer) return
  }

  const openCalendar = () => {
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.open()
      setTimeout(updateCalendarPosition, 0)
    }
  }

  const handleContainerClick = (e: React.MouseEvent) => {
    if (!editable) return
    e.stopPropagation()

    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.input.focus()
      openCalendar()
    }
  }

  const handleIconClick = (e: React.MouseEvent) => {
    if (!editable) return
    e.stopPropagation()
    e.preventDefault()
    openCalendar()
  }

  const formatDisplayDate = (dateStr: string | undefined | null) => {
    if (!dateStr) return ""
    const [year, month, day] = dateStr.split("-")
    return `${day}/${month}/${year}`
  }

  const cleanupFlatpickr = () => {
    try {
      if (flatpickrInstanceRef.current) {
        if (
          flatpickrInstanceRef.current.calendarContainer &&
          document.body.contains(flatpickrInstanceRef.current.calendarContainer)
        ) {
          document.body.removeChild(flatpickrInstanceRef.current.calendarContainer)
        }

        if (flatpickrInstanceRef.current.destroy && typeof flatpickrInstanceRef.current.destroy === "function") {
          flatpickrInstanceRef.current.destroy()
        }

        flatpickrInstanceRef.current = null
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      // @ts-ignore
      window.flatpickr.localize(Spanish)
    }

    if (flatpickrRef.current?.flatpickr) {
      flatpickrInstanceRef.current = flatpickrRef.current.flatpickr
      flatpickrInstanceRef.current.calendarContainer.classList.add("date-field-calendar")

      if (!document.body.contains(flatpickrInstanceRef.current.calendarContainer)) {
        document.body.appendChild(flatpickrInstanceRef.current.calendarContainer)
      }

      const originalPositionCalendar = flatpickrInstanceRef.current.positionCalendar
      flatpickrInstanceRef.current.positionCalendar = function (...args: any[]) {
        originalPositionCalendar.apply(this, args)
        updateCalendarPosition()
      }
    }

    return cleanupFlatpickr
  }, [])

  return (
    <div className="w-full relative date-field-container">
      {title && (
        <div className="flex mb-3">
          <span className="text-md text-[#4A5568] font-medium">{title}</span>
          {tooltip && (
            <div className="relative group inline-block ml-1 mt-1">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-pointer" >
                  <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className="absolute text-center z-50 hidden group-hover:block top-full mt-2 left-7 -translate-x-1/2 px-3 py-2 bg-[#171923] text-white text-sm rounded-lg shadow-lg max-w-[412px] w-max break-words" >
                {tooltip}
              </div>
            </div>
          )}
          <div className="flex ml-2.5 items-center">
            {resumeValue !== undefined && (
              <div className={`${typeof resumeValue === "number" ? getResultColorClass(resumeValue) : "bg-[#EDF2F7] text-[#4A5568]"} w-auto px-1 h-[16px] text-center rounded-[2px] text-xs font-normal`}>
                  {typeof resumeValue === "number" ? resumeValue.toFixed(1) : resumeValue}
              </div>
            )}
          </div>
        </div>
      )}
      <div ref={containerRef} className="relative" onClick={handleContainerClick}>
        <label
          className={`absolute left-3 text-[12px] transition-all duration-300 pointer-events-none z-10
            ${!isEmpty ? "top-[-10px] text-[12px] text-[#A0AEC0] bg-white px-1" : "top-[10px] text-[12px] text-[#A0AEC0]"}`}
        >
          {placeholder}
        </label>

        <div className="w-full h-[40px] font-normal text-[#171923] rounded-[10px] border cursor-pointer flex justify-between items-center px-3">
          <Flatpickr
            ref={flatpickrRef}
            options={{
              mode: "single",
              dateFormat: "d/m/Y",
              locale: Spanish,
              showMonths: 1,
              minDate: null,
              maxDate: "today",
              closeOnSelect: true,
              onChange: handleDateChange,
              clickOpens: true,
              monthSelectorType: "dropdown",
            }}
            value={model[campoId] ? formatDisplayDate(model[campoId]) : ""}
            onChange={handleDateChange}
            className="text-[14px] font-normal text-[#000000] bg-transparent w-full outline-none cursor-pointer border-none p-0"
            required={required}
            disabled={!editable}
          />
          <HiOutlineCalendarDays className="text-[#171923] cursor-pointer" onClick={handleIconClick} />
        </div>
      </div>
    </div>
  )
}

export default DateField
